import { createRoleMutation, updateRoleMutation } from "@/api/role";
import { OrgModule } from "@/store/modules/org";
import { Component, Prop } from "vue-property-decorator";

import { RoleTypeEnum } from "@/api/common-types";
import FormEmail from "@/components/forms/inputs/FormEmail.component";
import FormSelect from "@/components/forms/inputs/FormSelect.component";
import UserPermissions from "@/mixins/UserPermissions.mixin";
import styles from "./OrgUserInvite.view.module.scss";
import * as tsx from "vue-tsx-support";
import {
  BContainer,
  BRow,
  BAlert,
  BForm,
  BButton,
  BSpinner,
} from "@/lib/typed-bootstrap";

interface RoleOption {
  value: string | null;
  text: string;
  disabled: boolean;
}

@Component
export default class OrgUserInvite extends UserPermissions {
  _tsx!: tsx.DeclareProps<{}> & tsx.DeclareOnEvents<{}>;

  @Prop({ required: false }) readonly email: string | undefined;
  @Prop({ required: false }) readonly role: string | undefined;
  @Prop({ required: false }) readonly roleId: string | undefined;

  isLoading = false;
  errorMessage = "";
  selectedRole: string | null = null;
  roleOptions: RoleOption[] = [
    { value: null, text: "", disabled: false },
    { value: "OWNER", text: "", disabled: true },
    { value: "ADMIN", text: "", disabled: true },
    { value: "CONTENT", text: "", disabled: true },
    { value: "DEV", text: "", disabled: true },
    { value: "GUEST", text: "", disabled: true },
  ];

  inviteeEmail = "";

  get isEditMode(): boolean {
    return this.$route.params.action === "edit";
  }

  get buttonCopy(): string {
    return this.isEditMode
      ? this.$t("global_cta_update").toString()
      : this.$t("form_cta_invite").toString();
  }

  get emailEditingIsDisabled(): boolean {
    return this.isEditMode;
  }

  get shouldValidateEmail(): boolean {
    return !this.isEditMode;
  }

  get showErrorMessage(): boolean {
    return !!this.errorMessage;
  }

  async onSubmit(): Promise<void> {
    try {
      if (!this.selectedRole) {
        this.errorMessage = this.$t("global_org_invite_form_error").toString();
        return;
      }

      if (!this.inviteeEmail) {
        this.errorMessage = this.$t(
          "global_email_error_format_email",
        ).toString();
        return;
      }

      this.errorMessage = "";
      this.isLoading = true;

      if (this.isEditMode) {
        if (this.roleId) {
          await updateRoleMutation(
            {
              input: {
                id: this.roleId,
                type: this.selectedRole as RoleTypeEnum,
              },
            },
            this.orgId,
            true,
          );
        }
      } else {
        const input = {
          userEmail: this.inviteeEmail,
          type: this.selectedRole,
        };

        await createRoleMutation(input);
      }

      this.$router.push({
        name: "org-users",
        params: { orgId: OrgModule.id },
      });
    } catch (err: any) {
      this.errorMessage = err.message || "An unexpected error occurred.";
    } finally {
      this.isLoading = false;
    }
  }

  mounted() {
    this.initialize();
    this.roleOptions = [
      {
        value: null,
        text: this.$t("org_users_invite_role_placeholder").toString(),
        disabled: false,
      },
      {
        value: "OWNER",
        text: this.$t("org_users_invite_role_owner").toString(),
        disabled: !this.canManageOrg,
      },
      {
        value: "ADMIN",
        text: this.$t("org_users_invite_role_admin").toString(),
        disabled: !this.canManageOrg,
      },
      {
        value: "CONTENT",
        text: this.$t("org_users_invite_role_content").toString(),
        disabled: !this.canManageOrg,
      },
      {
        value: "DEV",
        text: this.$t("org_users_invite_role_dev").toString(),
        disabled: !this.canManageOrg,
      },
      {
        value: "GUEST",
        text: this.$t("org_users_invite_role_guest").toString(),
        disabled: !this.canManageOrg,
      },
    ];
  }

  initialize() {
    this.inviteeEmail = this.email ?? "";
    this.selectedRole = this.role ?? null;
  }

  render() {
    return (
      <BContainer class="default__view-setup">
        <BRow class="justify-content-center">
          <div class={styles.inviteForm}>
            <h1 class={styles.title}>
              {this.$t("org_users_invite_title").toString()}
            </h1>
            <div class={styles.subtitle}>
              {this.$t("org_users_invite_subtitle").toString()}
            </div>
            <BAlert show={this.showErrorMessage} variant="danger">
              {this.errorMessage}
            </BAlert>

            <BForm
              class="default__inline-form"
              acceptCharset="UTF-8"
              role="form"
              novalidate
              onSubmit={(e: Event) => {
                e.preventDefault();
                e.stopPropagation();
                this.onSubmit();
              }}
            >
              <FormEmail
                value={this.inviteeEmail}
                onInput={(val: string) => (this.inviteeEmail = val)}
                label={this.$t("form_input_label_invite_email").toString()}
                isDisabled={this.emailEditingIsDisabled}
                placeholderText={this.$t(
                  "form_placeholder_business_email",
                ).toString()}
                required={!this.isEditMode}
              />

              <FormSelect
                id="roleSelect"
                value={this.selectedRole}
                onInput={(val: string | null) => (this.selectedRole = val)}
                label={this.$t("form_input_label_invite_role").toString()}
                selectOptions={this.roleOptions}
              />

              <BButton
                type="submit"
                class="default__button mt-5"
                data-testid="inviteButton"
                disabled={this.isLoading}
                size="lg"
                block
              >
                {this.isLoading && (
                  <div class="d-flex default__spinner" role="status">
                    <BSpinner
                      class="ml-auto"
                      type="grow"
                      label="Spinning"
                      small
                    />
                  </div>
                )}
                <div class="button__text">{this.buttonCopy}</div>
              </BButton>
            </BForm>
          </div>
        </BRow>
      </BContainer>
    );
  }
}
